import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  is90sCartoonFlow,
  isCaricaturePortraitFlow,
  isSnapPrintFlow,
  readSelectedAiSelfieArtFromStorage,
  resolveFifaStationSlug,
  resolveHobbyCaricatureRouteSlug,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import { isFifaWorldCup2026Station, resolveStationRouteSlug } from "../../../utils/stationFlowStorage";
import { bgImages } from "../../../constants";
import Photo from "./../../../assets/img/photo.png";
import Upload from "./../../../assets/img/upload.png";
import TakePhotoModal from "../../../modal/TakePhotoModal";
import StationThemeLayout from "../../../layouts/StationThemeLayout";


const ChooseOption = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [photoModal, setPhotoModal] = useState(false)

  // Get selected station (child) first, then fall back to legacy stationData
  const stationData = useMemo(() => {
    const selectedRaw = localStorage.getItem("selectedStation");
    const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
    const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
    return fromSelected || fromStorage;
  }, [location.state]);

  const selectedArt = useMemo(
    () =>
      (location.state as { selectedArt?: unknown } | null)?.selectedArt ||
      readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );

  // After /:category/all, inner pages use slugified selectedAiSelfieArt.name.
  const stationSlug = resolveStationRouteSlug(stationData, slug, selectedArt as any);
  const isCaricaturePortrait = isCaricaturePortraitFlow({
    slug: slug || stationSlug,
    stationType: stationData?.type,
    artName: (selectedArt as { name?: string } | null)?.name,
  });
  const isSnapPrint = isSnapPrintFlow({
    slug: slug || stationSlug,
    stationType: stationData?.type,
    artName: (selectedArt as { name?: string } | null)?.name,
  });
  const is90sCartoon = is90sCartoonFlow({
    slug: slug || stationSlug,
    stationType: stationData?.type,
    artName: (selectedArt as { name?: string } | null)?.name,
  });

  useEffect(() => {
    const selectedArt = readSelectedAiSelfieArtFromStorage();
    const isHobby =
      stationData?.type === "hobby-caricature" ||
      shouldUseHobbyCaricatureFlowForStoredAiSelfieArt();
    const isFifa = isFifaWorldCup2026Station(stationData, selectedArt);

    if (isHobby) {
      const hobbySlug = resolveHobbyCaricatureRouteSlug(slug || stationSlug);
      navigate(`/${hobbySlug}/select-people`, { replace: true });
      return;
    }

    if (isFifa) {
      const fifaSlug = resolveFifaStationSlug(stationData, selectedArt);
      navigate(`/${fifaSlug}/select-fifa-card`, { replace: true });
    }
    // Both `is_caricature_hobby` and `is_fifa` false → stay on choose-option
    // (take photo / upload → image-transformation).
  }, [navigate, stationData]);

  const cartoonBackground = is90sCartoon
    ? (selectedArt as { inner_bg_image?: string | null } | null)?.inner_bg_image?.trim() ||
      (selectedArt as { background_image?: string | null } | null)?.background_image?.trim() ||
      bgImages.cartoon
    : undefined;

  return (
    <>
      <StationThemeLayout
        className={`chooseOptionSection p-0${is90sCartoon ? " selectPeopleCartoonTheme" : ""}`}
        station={stationData}
        showStationLogo
        backgroundOverride={cartoonBackground}
        onBack={
          isCaricaturePortrait
            ? () => navigate(`/${stationSlug}/idle`)
            : is90sCartoon
              ? () => navigate(`/${stationSlug}/select-people-cartoon`)
              : undefined
        }
      >
        <div className="h-100 chooseCard">
          <div className="chooseContainer">
            <div className="heading">Choose an Option</div>
            <div className="optionSection">
              <div className="singleOption" onClick={() => setPhotoModal(true)}>
                <img src={Photo} alt="Take a photo" className="optionIMG" />
                <div className="optionTxt">Take a photo</div>
              </div>
              <div className="singleOption" onClick={() => navigate(`/${stationSlug}/upload-image-qr-code`)}>
                <img src={Upload} alt="Upload a photo" className="optionIMG" />
                <div className="optionTxt">Upload a photo</div>
              </div>
            </div>
          </div>
        </div>
      </StationThemeLayout>

      {photoModal &&
        <TakePhotoModal
          show={photoModal}
          onHide={() => setPhotoModal(false)}
          // Print Your Own: don't strip BG on kiosk — upload only, then /bg-remove.
          skipBackgroundRemoval={isSnapPrint}
        />
      }
    </>
  );
};

export default ChooseOption;
